import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature, ReportPopupShareTab } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonShareEmbedComponent from 'webapp/components/common/share/embed';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ReportsShareDownloadTabComponent from 'webapp/components/reports/share/download-tab';
import ReportsShareLinkTabComponent from 'webapp/components/reports/share/link-tab';
import ReportsShareSendTabComponent from 'webapp/components/reports/share/send-tab';

import type Report from 'webapp/models-custom/report';

export { ReportPopupShareTab };

interface ShareTabItem {
	id: ReportPopupShareTab;
	text: string;
	locked?: boolean;
}

export default class ReportPopupShareComponent extends PopupInnerModalComponent<PopupData.Report.Share> {
	@service('billing') declare billingService: Service.Billing;

	@tracked selectedTab: ReportPopupShareTab | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// only set to first tab when we haven't set the tab explicitly on popup init
		let tabId = this.popupData.selectedTab;
		const targetTab = tabId ? this.getTab(tabId) : this.shownTabs[0];
		if (!targetTab || targetTab.locked) {
			const availableTab = this.shownTabs.find((t) => !t.locked);
			tabId = availableTab?.id;
		} else {
			tabId = targetTab.id;
		}

		if (tabId) {
			this.openTab(tabId);
		} else {
			this.showSharingLimitationPopup();
			void this.args.model.close(); // no tabs anyways
		}
	}

	get model(): Report {
		return this.popupData.model;
	}

	get trackingOrigin() {
		return this.popupData.trackingOrigin;
	}

	get isShareableLinkTab(): boolean {
		return this.selectedTab === ReportPopupShareTab.LINK;
	}

	get isDownloadTab(): boolean {
		return this.selectedTab === ReportPopupShareTab.DOWNLOAD;
	}

	get isSendTab(): boolean {
		return this.selectedTab === ReportPopupShareTab.SEND;
	}

	get isEmbedTab(): boolean {
		return this.selectedTab === ReportPopupShareTab.EMBED;
	}

	get shownTabs(): ShareTabItem[] {
		const tabs: ShareTabItem[] = [],
			isSharingLocked = this.isSharingTabsLocked,
			model = this.model;

		tabs.push({
			id: ReportPopupShareTab.LINK,
			text: 'Shareable link',
			locked: isSharingLocked,
		});

		tabs.push({
			id: ReportPopupShareTab.DOWNLOAD,
			text: 'Download PDF',
			locked: isSharingLocked,
		});

		if (model && !model.isTemplate) {
			tabs.push({
				id: ReportPopupShareTab.SEND,
				text: 'Send Report',
				locked: isSharingLocked,
			});
		}

		tabs.push({
			id: ReportPopupShareTab.EMBED,
			text: 'Embed',
			locked: isSharingLocked,
		});

		return tabs;
	}

	get isSharingTabsLocked() {
		return !this.billingService.sharingEnabled;
	}

	private getTab(tabId: ReportPopupShareTab) {
		return this.shownTabs.find((tab) => tab.id === tabId);
	}

	private showSharingLimitationPopup() {
		this.billingService.handleLimitations(BillingFeature.SHARING);
	}

	@action
	openTab(tabId: ReportPopupShareTab): void {
		const targetTab = this.getTab(tabId);
		if (targetTab?.locked) {
			this.showSharingLimitationPopup();
			return;
		}
		this.selectedTab = tabId;
		this.scheduleReposition();
	}

	@action
	openLinkTab() {
		this.openTab(ReportPopupShareTab.LINK);
	}

	<template>
		<div class="title">
			<BoardImagePreviewComponent
				@image={{this.model.image}}
				@fallbackImage={{this.model.fallbackImage}}
				@color={{this.model.color}}
				@count={{this.model.slideCount}}
			/>
			<span>Share '{{this.model.title}}'</span>
		</div>

		<ul class="modalTabs modalTabs-2">
			{{#each this.shownTabs as |tab|}}
				<li
					class="navigation-button
						{{if (eq this.selectedTab tab.id) 'navigation-button--selected'}}"
				>
					<a role="button" {{on "click" (fn this.openTab tab.id)}}>
						{{tab.text}}
						{{#if tab.locked}}
							<CommonUiIcon @name="Lock" @size="sm" />
						{{/if}}
					</a>
				</li>
			{{/each}}
		</ul>

		<div class="content">

			<ReportsShareLinkTabComponent
				@model={{this.model}}
				@isContentShown={{this.isShareableLinkTab}}
				@trackingOrigin={{this.popupData.trackingOrigin}}
				@onContentChange={{this.scheduleReposition}}
				@onClose={{this.close}}
			/>

			<ReportsShareDownloadTabComponent
				@model={{this.model}}
				@isContentShown={{this.isDownloadTab}}
				@onContentChange={{this.scheduleReposition}}
				@onClose={{this.close}}
			/>

			<ReportsShareSendTabComponent
				@model={{this.model}}
				@isContentShown={{this.isSendTab}}
				@trackingOrigin={{this.trackingOrigin}}
				@onContentChange={{this.scheduleReposition}}
				@onClose={{this.close}}
			/>

			<CommonShareEmbedComponent
				@model={{this.model}}
				@isContentShown={{this.isEmbedTab}}
				@openLinkTab={{this.openLinkTab}}
				@onClose={{this.close}}
			/>

		</div>

		<div class="filler"></div>
	</template>
}
